/**
 * Enin company lookup — shared pure helpers and types.
 *
 * Used by the `enin_lookup` flow component: `resolveEninCompanyId` builds the
 * country-prefixed Enin company id from a client's orgId + country, and
 * `extractEninStatus` derives a compact risk verdict from the `company_flags`
 * endpoint payload. Both are pure (no I/O) so they can be unit-tested and
 * shared between apps/api and apps/web.
 */

/**
 * Enin supports four Nordic schemas. Each country requires its own orgnr format:
 *   NO — 9 digits, no separators            (e.g. NO923609016)
 *   SE — 6 digits, dash, 4 digits           (e.g. SE556036-0793)
 *   DK — 8 digits, no separators            (e.g. DK61056416)
 *   FI — 7 digits, dash, 1 digit            (e.g. FI0112038-9)
 *
 * The formats are re-encoded here (rather than imported from the API-side
 * enin-service) because this package must stay free of app imports.
 */
export const ENIN_SUPPORTED_COUNTRIES = ["NO", "SE", "DK", "FI"] as const;
export type EninCompanyCountry = (typeof ENIN_SUPPORTED_COUNTRIES)[number];

const ENIN_ORG_NR_FORMATS: Record<EninCompanyCountry, RegExp> = {
  NO: /^\d{9}$/,
  SE: /^\d{6}-\d{4}$/,
  DK: /^\d{8}$/,
  FI: /^\d{7}-\d$/,
};

/**
 * Build the country-prefixed Enin companyId (e.g. "NO923609016") from a client
 * orgId + country. Accepts digits-only SE/FI variants and normalises them to
 * the dashed form. Returns null when the id cannot be constructed/validated —
 * null is the signal for the UI/AI to fall back to name search.
 */
export function resolveEninCompanyId(
  orgId: string | null | undefined,
  country: string | null | undefined,
): string | null {
  if (!orgId || !country) return null;

  const countryCode = country.trim().toUpperCase();
  if (!(ENIN_SUPPORTED_COUNTRIES as readonly string[]).includes(countryCode)) return null;
  const supported = countryCode as EninCompanyCountry;

  let orgNr = orgId.replace(/\s+/g, "").toUpperCase();
  if (orgNr.startsWith(supported)) orgNr = orgNr.slice(2);

  if (supported === "SE" && /^\d{10}$/.test(orgNr)) {
    orgNr = `${orgNr.slice(0, 6)}-${orgNr.slice(6)}`;
  }
  if (supported === "FI" && /^\d{8}$/.test(orgNr)) {
    orgNr = `${orgNr.slice(0, 7)}-${orgNr.slice(7)}`;
  }

  if (!ENIN_ORG_NR_FORMATS[supported].test(orgNr)) return null;
  return `${supported}${orgNr}`;
}

// ─── Name-search fallback (company picker) ───────────────────────────────────

export type EninCompanySearchHit = {
  /** Country-prefixed Enin id, e.g. "NO923609016". */
  companyId: string;
  orgNr: string;
  name: string;
  country: EninCompanyCountry;
};

export type EninCompanySearchResponse =
  | { success: true; results: EninCompanySearchHit[] }
  | { success: false; error: string };

// ─── Risk verdict derived from the company_flags endpoint ────────────────────

export type EninFlag = {
  code: string;
  description: string | null;
  isActive: boolean;
  isNegative: boolean;
  startDate: string | null;
};

export type EninStatus = {
  hasActiveNegativeFlags: boolean;
  flags: EninFlag[];
};

function asNonEmptyString(value: unknown): string | null {
  return typeof value === "string" && value.trim() !== "" ? value : null;
}

function unwrapFlagItems(payload: unknown): unknown[] {
  if (Array.isArray(payload)) return payload;
  if (payload && typeof payload === "object") {
    const obj = payload as Record<string, unknown>;
    for (const key of ["company_flag_collection", "data", "flags"]) {
      const value = obj[key];
      if (Array.isArray(value)) return value;
    }
  }
  return [];
}

/**
 * Derive a compact risk verdict from the Enin `company_flags` payload.
 *
 * OPEN ITEM: the exact live shape of the `company_flags` response is
 * unverified — the field names below are defensive heuristics that must be
 * validated against a real API response and refined. The `EninStatus` contract
 * (what callers consume) is stable; only the extraction heuristics may change.
 *
 * Heuristics, erring toward surfacing risk (the right failure mode for a
 * compliance check):
 * - a flag is active when `is_active === true`, or when no `is_active` field
 *   exists and the flag has no end date (open-ended = still active);
 * - a flag is negative when an explicit boolean/sentiment field says so, or
 *   when no sentiment field exists at all (the company_flag endpoint is a
 *   risk-flag feed — presence alone is treated as an adverse signal).
 */
export function extractEninStatus(companyFlagsPayload: unknown): EninStatus {
  const items = unwrapFlagItems(companyFlagsPayload);
  const flags: EninFlag[] = [];
  const seen = new Set<string>();

  for (const item of items) {
    if (!item || typeof item !== "object" || Array.isArray(item)) continue;
    const row = item as Record<string, unknown>;
    // Composite rows wrap the flag as { company_flag: {...} }
    const raw =
      row.company_flag && typeof row.company_flag === "object" && !Array.isArray(row.company_flag)
        ? (row.company_flag as Record<string, unknown>)
        : row;

    // Composite rows carry the readable type as a sibling object.
    const flagType =
      row.company_flag_type &&
      typeof row.company_flag_type === "object" &&
      !Array.isArray(row.company_flag_type)
        ? (row.company_flag_type as Record<string, unknown>)
        : null;

    const code =
      asNonEmptyString(flagType?.company_flag_type_key) ??
      asNonEmptyString(raw.flag_type) ??
      asNonEmptyString(raw.company_flag_type) ??
      asNonEmptyString(raw.code) ??
      asNonEmptyString(raw.flag) ??
      "unknown";
    const description =
      asNonEmptyString(flagType?.company_flag_type_description) ??
      asNonEmptyString(raw.flag_description) ??
      asNonEmptyString(raw.description);
    const startDate =
      asNonEmptyString(raw.flag_date)?.slice(0, 10) ??
      asNonEmptyString(raw.flag_start_date) ??
      asNonEmptyString(raw.start_date);
    const endDate =
      asNonEmptyString(raw.flag_end_date) ??
      asNonEmptyString(raw.end_date) ??
      asNonEmptyString(raw.valid_to);

    const isActive = raw.is_active === true || (raw.is_active === undefined && endDate === null);

    const sentiment =
      asNonEmptyString(raw.flag_sentiment) ??
      asNonEmptyString(raw.sentiment) ??
      asNonEmptyString(raw.flag_severity);
    const isNegative =
      raw.is_negative === true ||
      (raw.is_negative === undefined &&
        (sentiment === null || ["negative", "high"].includes(sentiment.toLowerCase())));

    const dedupeKey = `${code}|${startDate ?? ""}`;
    if (seen.has(dedupeKey)) continue;
    seen.add(dedupeKey);

    flags.push({ code, description, isActive, isNegative, startDate });
  }

  return {
    hasActiveNegativeFlags: flags.some((f) => f.isActive && f.isNegative),
    flags,
  };
}
